import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatGBP, formatDateUK, apiError } from "@/lib/api";
import { todayUK } from "@/components/PaymentChoice";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Send, AlertTriangle, Check } from "lucide-react";

export const MOVES_IN = [["cash_sales", "Cash sales (Z reports)"], ["cash_customer_collections", "Customer account payments"], ["other_cash_receipts", "Other cash in"]];
export const MOVES_OUT = [["cash_supplier_payments", "Supplier payments"], ["cash_expenses", "Cash expenses"], ["cash_refunds", "Cash refunds (Z reports)"], ["bank_deposits", "Paid into bank"], ["other_cash_taken_out", "Other cash taken out"]];
const STEPS = ["Review Z report", "Confirm cash movements", "Count cash", "Explain difference", "Submit"];
const OPENERS = ["owner", "store_manager"];

export function Breakdown({ opening, m, expected }) {
  const line = (k, l, sign) => (
    <div key={k} className="flex justify-between text-sm py-1"><span className="text-navy/60">{l}</span><span className="font-mono text-navy" data-testid={`cash-move-${k}`}>{sign}{formatGBP(m[k] || 0)}</span></div>
  );
  return (
    <div className="rounded-xl border border-navy/10 bg-white p-4" data-testid="cash-breakdown">
      <div className="flex justify-between text-sm py-1"><span className="text-navy/60">Opening cash</span><span className="font-mono text-navy" data-testid="cash-opening">{formatGBP(opening)}</span></div>
      {MOVES_IN.map(([k, l]) => line(k, l, "+"))}
      {MOVES_OUT.map(([k, l]) => line(k, l, "−"))}
      <div className="flex justify-between pt-2 mt-1 border-t border-navy/10"><span className="font-semibold text-navy text-sm">Expected in the till</span><span className="font-mono font-bold text-navy" data-testid="cash-expected">{formatGBP(expected)}</span></div>
      {m.paid_in_full_cash_invoices_included_via_payment?.count > 0 && (
        <p className="text-[11px] text-navy/45 pt-1">Includes {m.paid_in_full_cash_invoices_included_via_payment.count} invoice(s) paid in cash with the purchase — counted once.</p>
      )}
      {m.cash_refunds_already_in_z_cash > 0 && <p className="text-[11px] text-navy/45 pt-1" data-testid="cash-refunds-in-z">{formatGBP(m.cash_refunds_already_in_z_cash)} cash refunds are already taken off the Z cash figure — not taken off again.</p>}
      {m.card_and_other_refunds_not_cash > 0 && <p className="text-[11px] text-navy/45 pt-1" data-testid="cash-card-refunds-note">{formatGBP(m.card_and_other_refunds_not_cash)} card/other refunds did not come out of the till.</p>}
    </div>
  );
}

const ukToDate = (s) => { const [d, m, y] = s.split("/").map(Number); return new Date(y, m - 1, d); };
const daysAgo = (s) => Math.round((ukToDate(todayUK()) - ukToDate(s)) / 86400000);

function DateCheck({ day, isManager, reason, setReason }) {
  const ago = daysAgo(day);
  if (ago < 0 || ukToDate(day) < new Date(2025, 0, 1)) {
    return <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="cash-date-invalid">{ago < 0 ? "This date is in the future." : "Dates before 01/01/2025 are not accepted — check the year."}</p>;
  }
  if (ago <= 1) return null;
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 space-y-2" data-testid="cash-date-warning">
      <p className="flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0" /> This date is {ago} days ago. Check the day and the year are right.</p>
      {ago > 14 && (isManager
        ? <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="bg-white" placeholder="Reason for this historical correction (required)" data-testid="cash-historical-reason-input" />
        : <p className="text-xs">Dates more than 14 days ago can only be entered by a manager as a correction.</p>)}
    </div>
  );
}

function OpeningBalance({ storeId, day, onDone }) {
  const [amt, setAmt] = useState("");
  const [note, setNote] = useState("");
  const go = async () => {
    try { await api.post("/cash-closings/opening-balance", { store_id: parseInt(storeId), start_date: day, amount: parseFloat(amt), note }); toast.success("Opening cash set"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-xl border border-navy/10 bg-white p-4 space-y-2" data-testid="cash-opening-balance-form">
      <p className="text-sm text-navy">First day for this store: set the cash in the till at the start of {day}. It is recorded with your name and the time.</p>
      <Input inputMode="decimal" value={amt} onChange={(e) => setAmt(e.target.value)} placeholder="0.00" className="gm-input font-mono" data-testid="cash-opening-balance-input" />
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="gm-input" data-testid="cash-opening-balance-note" />
      <button onClick={go} disabled={amt === ""} className="w-full gm-btn-navy disabled:opacity-40" data-testid="cash-opening-balance-btn">Set opening cash</button>
    </div>
  );
}

function ZStep({ exp, navigate }) {
  if (!exp.z_reports.length) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 space-y-2" data-testid="cash-no-z">
        <p>No Z report has been submitted for this day, so cash sales show £0.</p>
        <button onClick={() => navigate("/record-z")} className="underline" data-testid="cash-record-z-link">Record the Z report first</button>
      </div>
    );
  }
  return exp.z_reports.map((z) => (
    <div key={z.id} className="rounded-xl border border-navy/10 bg-white p-4 text-sm space-y-1" data-testid={`cash-z-${z.id}`}>
      <div className="flex justify-between font-semibold text-navy"><span>{z.register_ref}</span><span className="font-mono">{formatGBP(z.total_sales)}</span></div>
      <div className="flex justify-between"><span className="text-navy/60">Cash sales</span><span className="font-mono">{formatGBP(z.cash_sales)}</span></div>
      <div className="flex justify-between"><span className="text-navy/60">Cash refunds</span><span className="font-mono">{formatGBP(z.cash_refunds)}</span></div>
      <div className="flex justify-between"><span className="text-navy/60">Card / other refunds (not from till)</span><span className="font-mono">{formatGBP(z.card_refunds + z.other_refunds)}</span></div>
      {z.cash_refunds > 0 && <p className="text-[11px] text-navy/45">{z.refund_layout === "net" ? "Cash sales are already after refunds — refunds are not taken off again." : "Cash refunds are taken off the cash sales."}</p>}
    </div>
  ));
}

function Blocker({ exp, isManager, storeId, day, reload }) {
  if (exp.existing_id) return null;
  if (exp.later_closing_date) {
    return <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="cash-later-closing">Cash for this store is already closed for {formatDateUK(exp.later_closing_date)}, so an earlier day can't be closed now. Ask your manager.</p>;
  }
  if (exp.needs_opening_balance) {
    return isManager ? <OpeningBalance storeId={storeId} day={day} onDone={reload} />
      : <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="cash-needs-opening">No opening cash has been set for this store yet. Ask your manager to set the first-day opening cash.</p>;
  }
  if (exp.previous && exp.previous.status !== "approved") {
    return <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="cash-previous-pending">The cash closing for {formatDateUK(exp.previous.trading_date)} is not approved yet. Today's opening cash comes from it, so a manager must approve it first.</p>;
  }
  return null;
}

export default function CashClosingFormPage() {
  const { activeStoreId, stores, user } = useAuth();
  const navigate = useNavigate();
  const isManager = OPENERS.includes(user?.role);
  const [storeId, setStoreId] = useState(String(activeStoreId && activeStoreId !== "all" ? activeStoreId : stores[0]?.id || ""));
  const [day, setDay] = useState(todayUK());
  const [exp, setExp] = useState(null);
  const [step, setStep] = useState(0);
  const [counted, setCounted] = useState("");
  const [why, setWhy] = useState("");
  const [hist, setHist] = useState("");
  const [tick, setTick] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setExp(null); setStep(0);
    if (!storeId || !/^\d{2}\/\d{2}\/\d{4}$/.test(day)) { return; }
    let live = true;
    api.get(`/cash-closings/expected?store_id=${storeId}&trading_date=${day}`).then((r) => { if (live) setExp(r.data); }).catch((e) => { if (live) toast.error(apiError(e)); });
    return () => { live = false; };
  }, [storeId, day, tick]);

  const m = exp?.movements || {};
  const expected = exp?.expected_cash ?? 0;
  const diff = counted === "" ? null : Math.round(((parseFloat(counted) || 0) - expected) * 100) / 100;
  const material = diff !== null && Math.abs(diff) > (exp?.review_threshold || 5);
  const ok = exp && exp.expected_cash !== undefined && !exp.existing_id;
  const histOk = daysAgo(day) <= 14 || hist.trim().length >= 10;
  const canNext = [true, true, counted !== "", diff === 0 || why.trim().length >= 10][step];

  const submit = async () => {
    setSaving(true);
    try {
      const r = await api.post("/cash-closings", { store_id: parseInt(storeId), trading_date: day, counted_cash: parseFloat(counted), explanation: why, historical_reason: hist || null });
      toast.success(r.data.status === "approved" ? "Cash closed" : "Cash closed — sent to a manager to review");
      navigate(`/cash-closings/${r.data.id}`, { replace: true });
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 pb-4" data-testid="cash-closing-form-page">
      <div className="flex items-center gap-3">
        <button onClick={() => (step ? setStep(step - 1) : navigate(-1))} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-navy/40" aria-label="Back" data-testid="cash-back-btn"><ArrowLeft className="w-5 h-5" /></button>
        <div><h1 className="text-xl font-heading font-bold text-navy">Close the till cash</h1>
          {ok && <p className="text-xs text-navy/50" data-testid="cash-step-label">Step {step + 1} of 5 · {STEPS[step]}</p>}</div>
      </div>
      {step === 0 && (
        <div className="grid grid-cols-2 gap-3">
          {stores.length > 1 ? (
            <div className="space-y-1.5"><Label className="text-sm text-navy">Store</Label>
              <Select value={storeId} onValueChange={setStoreId}><SelectTrigger className="gm-input" data-testid="cash-store-select"><SelectValue /></SelectTrigger>
                <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}</SelectContent></Select></div>
          ) : <div />}
          <div className="space-y-1.5"><Label className="text-sm text-navy">Trading date</Label><Input value={day} onChange={(e) => setDay(e.target.value)} placeholder="DD/MM/YYYY" className="gm-input" data-testid="cash-date-input" /></div>
        </div>
      )}
      {step === 0 && /^\d{2}\/\d{2}\/\d{4}$/.test(day) && <DateCheck day={day} isManager={isManager} reason={hist} setReason={setHist} />}
      {exp?.existing_id && <button onClick={() => navigate(`/cash-closings/${exp.existing_id}`)} className="w-full rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 text-left" data-testid="cash-existing-notice">Cash for this day is already closed — tap to open it.</button>}
      {exp && <Blocker exp={exp} isManager={isManager} storeId={storeId} day={day} reload={() => setTick((t) => t + 1)} />}
      {ok && exp.opening_issue && <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="cash-opening-gap">{exp.opening_issue} You can still close today's cash; a manager must check the opening cash before it is approved.</p>}
      {ok && step === 0 && <ZStep exp={exp} navigate={navigate} />}
      {ok && step === 1 && <Breakdown opening={exp.opening_cash} m={m} expected={expected} />}
      {ok && step === 1 && expected < 0 && <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="cash-negative-expected">Expected cash is below zero — a cash payment or deposit is probably entered wrongly. Check the movements before counting.</p>}
      {ok && step === 2 && (
        <div className="space-y-1.5"><Label className="text-sm font-medium text-navy">Count the cash in the till now</Label>
          <Input inputMode="decimal" autoFocus value={counted} onChange={(e) => setCounted(e.target.value)} placeholder="0.00" className="gm-input font-mono text-lg" data-testid="cash-counted-input" />
          <p className="text-xs text-navy/50">Expected: <span className="font-mono">{formatGBP(expected)}</span></p></div>
      )}
      {ok && step >= 3 && diff !== null && (
        <div className={`rounded-xl p-3 text-sm ${diff === 0 ? "bg-emerald-50 text-emerald-800" : material ? "bg-red-50 text-red-800" : "bg-amber-50 text-amber-900"}`} data-testid="cash-difference">
          {diff === 0 ? <span className="flex gap-2"><Check className="w-4 h-4" /> Exactly right — nothing to explain.</span> : <span className="flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0" />{diff < 0 ? "Short" : "Over"} by {formatGBP(Math.abs(diff))}{material ? " — a manager will review this." : "."}</span>}
        </div>
      )}
      {ok && step === 3 && diff !== 0 && (
        <div className="space-y-1.5"><Label className="text-sm text-navy">What happened? (required)</Label>
          <Textarea value={why} onChange={(e) => setWhy(e.target.value)} rows={2} placeholder="e.g. £5 given as change from the float, not rung through" data-testid="cash-explanation-input" /></div>
      )}
      {ok && step === 4 && (
        <div className="rounded-xl border border-navy/10 bg-white p-4 text-sm space-y-1" data-testid="cash-submit-summary">
          <div className="flex justify-between"><span className="text-navy/60">Expected</span><span className="font-mono">{formatGBP(expected)}</span></div>
          <div className="flex justify-between"><span className="text-navy/60">Counted</span><span className="font-mono">{formatGBP(parseFloat(counted) || 0)}</span></div>
          {why && <p className="text-navy/70 pt-1">“{why}”</p>}
        </div>
      )}
      {ok && (step < 4
        ? <button onClick={() => setStep(step + 1)} disabled={!canNext || (step === 0 && !histOk)} className="w-full gm-btn-navy flex items-center justify-center gap-2 disabled:opacity-40" data-testid="cash-next-btn">{["Z report is right", "Movements are right", "Next", "Next"][step]} <ArrowRight className="w-4 h-4" /></button>
        : <button onClick={submit} disabled={saving} className="w-full gm-btn-gold flex items-center justify-center gap-2 disabled:opacity-40" data-testid="cash-submit-btn"><Send className="w-4 h-4" /> Submit cash closing</button>)}
    </div>
  );
}
